import React, { useState, useRef, useEffect } from 'react';
import Bounce from 'react-reveal/Bounce';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Form/Button';
import GoogleSignIn from '../components/Form/GoogleSignIn';
import TextField from '../components/Form/TextField';
import useAuth from '../hooks/useAuth';
import swal from 'sweetalert';
import { checkEmailForPasswordReset, sendOtpCode, loginUser } from '../api/auth';
import { Eye, EyeOff } from 'lucide-react';
import { Helmet } from 'react-helmet';

const LoadingModal = ({ text }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-xl p-6 shadow-xl flex flex-col items-center">
      <img src="/assets/loading.gif" alt="Loading..." className="w-16 h-16" />
      <p className="mt-4 text-paragaia font-medium">{text}</p>
    </div>
  </div>
);

const SignInScreen = () => {
  const [userInput, setUserInput] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signInUser, setUser } = useAuth();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { value, name } = e.target;
    setUserInput((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!userInput.email.trim()) newErrors.email = "L'email est requis.";
    if (!userInput.password.trim()) newErrors.password = "Le mot de passe est requis.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const user = await signInUser(userInput.email, userInput.password);
      if (!isMounted.current) return;

      if (user?.role === 'admin') {
        try {
          const { data } = await loginUser(userInput);
          if (!isMounted.current) return;

          if (data?.token) {
            localStorage.setItem('access_token', data.token);
            setUser(data.user);
            await swal("Bienvenue Admin!", "Connexion réussie!", "success");
            navigate(`/admin/${data.user.dashboardUrl}`);
            return;
          }
        } catch {
          if (isMounted.current) {
            await swal("Erreur!", "Erreur de connexion admin.", "error");
          }
          return;
        }
      } else {
        await swal("Bienvenue!", "Connexion réussie!", "success");
        navigate('/');
      }
    } catch (err) {
      if (!isMounted.current) return;
      let msg = "Erreur de connexion.";
      if (err.response?.status === 401) {
        const text = err.response.data?.message?.toLowerCase() || '';
        msg = text.includes('banned') ? "Votre compte a été banni. Veuillez contacter l’administrateur." : "Identifiants incorrects.";
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      await swal("Erreur!", msg, "error");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleForgetPassword = async () => {
    if (!userInput.email.trim()) {
      swal("Erreur!", "Veuillez entrer votre adresse e-mail d'abord.", "error");
      return;
    }

    try {
      const { data } = await checkEmailForPasswordReset(userInput.email);
      if (!isMounted.current) return;

      if (data.googleLogin) {
        swal("Erreur!", "Ce compte utilise la connexion Google. Vous ne pouvez pas réinitialiser le mot de passe.", "error");
      } else {
        await sendOtpCode(userInput.email);
        if (!isMounted.current) return;
        localStorage.setItem(`otp_sent_for_${userInput.email}`, Date.now().toString());
        navigate('/forgetpassword', { state: { email: userInput.email } });
      }
    } catch (err) {
      if (isMounted.current) {
        swal("Erreur!", err.response?.data?.message || "Erreur inconnue.", "error");
      }
    }
  };

  const Inputs = [
    { id: 1, type: 'email', placeholder: 'Email', value: userInput.email, name: 'email' },
    { id: 2, type: 'password', placeholder: 'Mot de passe', value: userInput.password, name: 'password' },
  ];

  return (

    <main className="relative min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-white to-paragaiaLight">
      {loading && <LoadingModal text="Connexion en cours..." />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl w-full p-6">
        <Bounce left>
          <div className="hidden md:flex flex-col justify-center items-center">
            <img className="w-full max-w-md object-contain" src="/assets/signin.gif" alt="Connexion" />
          </div>
        </Bounce>

        <Bounce right>
          <div className="flex flex-col justify-center items-center w-full relative">
            <div className="mb-6 z-0">
              <img src="/assets/favicon.png" alt="Logo Paragaïa" className="w-24 h-24 object-contain select-none" />
            </div>

            <form className="bg-white w-full max-w-md p-8 rounded-2xl shadow-2xl" onSubmit={handleSubmit}>
              <div className="flex flex-col space-y-6">
                {Inputs.map((input) => (
                  <div key={input.id} className="flex flex-col">
                    {input.name === 'password' ? (
                      <div className="relative">
                        <TextField
                          type={showPassword ? 'text' : 'password'}
                          placeholder={input.placeholder}
                          value={input.value}
                          name={input.name}
                          onChange={handleChange}
                        />
                        <span
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </span>
                      </div>
                    ) : (
                      <TextField
                        type={input.type}
                        placeholder={input.placeholder}
                        value={input.value}
                        name={input.name}
                        onChange={handleChange}
                      />
                    )}
                    {errors[input.name] && <p className="text-red-500 text-sm mt-1">{errors[input.name]}</p>}
                  </div>
                ))}
              </div>

              <Button
                text="Connexion"
                className="w-full mt-6 bg-paragaia text-white py-3 rounded-lg hover:bg-paragaiaLight transition poppins"
              />

              <p
                onClick={handleForgetPassword}
                className="text-sm text-center text-blue-500 hover:underline cursor-pointer mt-4"
              >
                Mot de passe oublié ?
              </p>

              <Link to="/signup">
                <p className="text-base text-paragaia text-center my-6 hover:underline poppins">
                  Créer un compte
                </p>
              </Link>

              <GoogleSignIn text="Connexion avec Google" />
            </form>
          </div>
        </Bounce>
      </div>
    </main>
  );
};

export default SignInScreen;
